import { pickT } from "@repo/shared";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { postFormData } from "@/lib/api/_client";
import { T } from "./image-editor.i18n";

type ImageEditorProps = {
  url: string;
  flowLanguage: string;
  onChange: (url: string) => void;
};

export function ImageEditor({ url, flowLanguage, onChange }: ImageEditorProps) {
  const [uploading, setUploading] = useState(false);
  const t = pickT(T, flowLanguage);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (including animated formats)
    if (
      ![
        "image/png",
        "image/svg+xml",
        "image/jpeg",
        "image/jpg",
        "image/gif",
        "image/webp",
      ].includes(file.type)
    ) {
      alert(t.invalidFileType);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t.fileTooLarge);
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", "flow-image");

      const data = await postFormData<{ url: string }>("/internal/uploads/upload", formData);
      onChange(data.url);
    } catch (_error) {
      alert(t.uploadFailed);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="image-upload">
          {t.image} <span className="text-destructive">*</span>
        </Label>
        <div className="flex gap-2">
          <Input
            id="image-upload"
            type="file"
            accept=".png,.svg,.jpg,.jpeg,.gif,.webp"
            onChange={handleFileChange}
            disabled={uploading}
          />
          {uploading && (
            <Button disabled size="sm">
              {t.uploading}
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{t.helpText}</p>
      </div>

      {url && (
        <div className="space-y-2">
          <Label>{t.preview}</Label>
          <div className="border rounded-lg p-4 bg-muted/50">
            <img src={url} alt={t.previewAlt} className="max-w-full max-h-64 mx-auto" />
          </div>
        </div>
      )}
    </div>
  );
}
